/**
 * Case, Parent, and Child types.
 */

import { BaseEntity, Address, CourtAddress, EmergencyContact, DateField } from './base';
import { Location } from './account';
import { Race, Sex, PrimaryLanguage, SpecialNeed, MaritalStatus, IncomeLevel, EducationLevel, EmploymentStatus, HousingStatus, PresentingConcern, VictimizationType, ExParteStatus, ServiceType, ServiceClassification, ReferralSource, ReasonForSupervision } from './demographics';
import { ProgramType } from './caseManagement';

// ============================================================================
// CASE
// ============================================================================

export interface Case extends BaseEntity {
  status: CaseStatus;
  identifier?: string;
  parents?: Record<string, Parent>;
  parentIds?: string[];
  children?: Record<string, Child>;
  childIds?: string[];
  accountId: string;
  location?: Location;
  locationId?: string;
  createdBy: string;
  contractId?: string;
  notes?: string;
  numberOfCompletedVisits?: number;
  numberOfCanceledVisits?: number;
  numberOfFinishedVisits?: number;
  numberOfScheduledVisits?: number;
  /** Breakdown of no-shows by responsible party */
  noShowStats?: NoShowStats;
  inquiryId?: string;
  courtAddress?: CourtAddress;

  // Demographics fields for grant reporting
  reasonForSupervision?: ReasonForSupervision;
  reasonForSupervisionOther?: string;
  /** Presenting concerns for the case (multi-select) */
  presentingConcerns?: PresentingConcern[];
  presentingConcernsOther?: string;
  /** @deprecated Use referralSourceType instead for structured data */
  referralSource?: string;
  /** Structured referral source type */
  referralSourceType?: ReferralSource;
  /** Additional details when referralSourceType is 'other' */
  referralSourceOther?: string;
  referralDate?: DateField;
  /** @deprecated Use serviceTypes array instead */
  serviceType?: ServiceType;
  /** Service types for this case - supports multiple services */
  serviceTypes?: ServiceType[];
  /** Service classifications (DV, Sexual Assault, DSS) for applicable service types */
  serviceClassifications?: ServiceClassification[];
  intakeDate?: DateField;
  scheduledOrientationDate?: DateField;

  // Court/Legal information
  isCourtOrdered?: boolean;
  /** @deprecated Use hasDVO instead */
  hasDV50B?: boolean;
  /** @deprecated Use hasRO instead */
  has50BProtectionOrder?: boolean;
  /** @deprecated Use roExpirationDate instead */
  protectionOrder50BDate?: DateField;
  /** @deprecated Use hasTempOrders instead */
  has50COrder?: boolean;
  /** @deprecated Use tempOrdersExpirationDate instead */
  order50CDate?: DateField;

  // Universal order types (state-agnostic)
  /** Domestic Violence Order (DVO) */
  hasDVO?: boolean;
  dvoExpirationDate?: DateField;
  /** Temporary Restraining Order (TRO) */
  hasTRO?: boolean;
  troExpirationDate?: DateField;
  /** Restraining Order (RO) */
  hasRO?: boolean;
  roExpirationDate?: DateField;
  /** Temporary Orders */
  hasTempOrders?: boolean;
  tempOrdersExpirationDate?: DateField;

  exParteStatus?: ExParteStatus;
  judgeName?: string;

  // Safety and risk information
  safetyInformation?: string;

  // Victimization information (case-level)
  primaryVictimizationType?: VictimizationType;
  primaryVictimizationOther?: string;
  offenderName?: string;
  secondaryVictimizationType?: VictimizationType;
  secondaryVictimizationOther?: string;
  secondaryOffenderName?: string;

  // Legal status
  allegationAgainstSelf?: boolean;
  chargesPendingAgainstSelf?: boolean;

  // Counseling/Therapy information
  inCounseling?: boolean;
  courtOrderedCounseling?: boolean;
  interestedInTherapeuticVisits?: boolean;

  // Program type for case categorization (supervised visitation, case management, etc.)
  // Deprecated: Use programTypes array instead
  programType?: ProgramType;

  // Program types for case categorization - cases can belong to multiple programs
  programTypes?: ProgramType[];

  // User IDs of case managers assigned to this case
  assignedCaseManagerIds?: string[];

  // Timestamp when case was closed (automatically set when status changes to 'closed')
  closedAt?: DateField;

  // Notes explaining why the case was closed (e.g., "Terminated due to incident", "No contact from parties", etc.)
  closureNotes?: string;

  /** Parent confirmation requirement override for this case.
   *  - 'require': parents must accept/decline visits
   *  - 'skip': visits go straight to scheduled without parent confirmation
   *  - 'default' or undefined: use the account-level setting
   */
  parentConfirmation?: 'require' | 'skip' | 'default';
}

export type CaseStatus = 'open' | 'closed' | 'active' | 'inactive' | 'pending' | 'suspended';
export type CaseType = 'supervision' | 'custody' | 'visitation' | 'guardianship' | 'adoption' | 'other';

/** Statistics tracking no-shows by responsible party */
export interface NoShowStats {
  /** Total number of no-shows */
  total: number;
  /** No-shows by the visiting parent */
  visitingParent: number;
  /** No-shows by the custodial parent */
  custodialParent: number;
  /** No-shows by both parents */
  bothParents: number;
  /** No-shows by the child */
  child: number;
  /** No-shows by the supervisor */
  supervisor: number;
  /** No-shows with other/unspecified reason */
  other: number;
}

export interface CaseSettings {
  allowPhotos: boolean;
  allowVideos: boolean;
  requireCheckIn: boolean;
  requireCheckOut: boolean;
  autoGenerateReports: boolean;
  requireParentConsent: boolean;
  allowRemoteSupervision: boolean;
  notificationPreferences: CaseNotificationPreferences;
  reportTemplates: string[];
  visitTemplates: string[];
}

export interface CaseNotificationPreferences {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  visitReminders: boolean;
  reportNotifications: boolean;
  incidentNotifications: boolean;
  parentNotifications: boolean;
}

// ============================================================================
// PARENT
// ============================================================================

export interface Parent extends BaseEntity {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  alternatePhone?: string;
  accountId: string;
  avatarUrl?: string;
  caseId: string;
  dob?: DateField;
  role?: string;
  relationshipToChild: RelationshipToChild | string;
  custodyStatus: CustodyStatus | string;
  stripeCustomerId?: string;
  address?: Address;
  emergencyContact?: EmergencyContact;
  preferences?: ParentPreferences;
  status?: ParentStatus;
  conversationSid?: string;
  userId?: string;

  // Demographics
  sex?: Sex;
  race?: Race;
  primaryLanguage?: PrimaryLanguage;
  primaryLanguageOther?: string;
  translatorNeeded?: boolean;
  maritalStatus?: MaritalStatus;
  incomeLevel?: IncomeLevel;
  educationLevel?: EducationLevel;
  employmentStatus?: EmploymentStatus;
  housingStatus?: HousingStatus;
  isRuralArea?: boolean;

  // Health information
  medicalProblems?: string;
  allergies?: string;
  mentalHealthConcerns?: boolean;
  isCurrentlyReceivingTherapy?: boolean;
  historyOfSubstanceAbuse?: boolean;

  // Legal/Attorney information (legacy fields - use attorneyIds instead)
  /** @deprecated Use attorneyIds instead */
  attorneyName?: string;
  /** @deprecated Use attorneyIds instead */
  attorneyPhone?: string;
  /** IDs of attorneys representing this parent */
  attorneyIds?: string[];

  // Visiting/Custodial role flags
  isVisitingParent?: boolean;
  isCustodialParent?: boolean;

  // DSS/Grant reporting fields
  dssReceivingServices?: boolean;
  dssReceivingTanf?: boolean;
  victimizationType?: VictimizationType;
  exParteStatus?: ExParteStatus;
  serviceType?: ServiceType;
  reasonForSupervision?: ReasonForSupervision;

  // Victimization information
  primaryVictimizationType?: VictimizationType;
  primaryVictimizationOther?: string;
  offenderName?: string;
  secondaryVictimizationType?: VictimizationType;
  secondaryVictimizationOther?: string;
  secondaryOffenderName?: string;

  notes?: string;
}

export enum RelationshipToChild {
  Mother = "mother",
  Father = "father",
  Stepmother = "stepmother",
  Stepfather = "stepfather",
  StepMother = "step-mother", // Mobile variant
  StepFather = "step-father", // Mobile variant
  Grandmother = "grandmother",
  Grandfather = "grandfather",
  Aunt = "aunt",
  Uncle = "uncle",
  Sister = "sister",
  Brother = "brother",
  Cousin = "cousin",
  Niece = "niece",
  Nephew = "nephew",
  Friend = "friend",
  FosterParent = "foster_parent",
  Guardian = "guardian",
  Other = "other",
}

export const RELATIONSHIP_LABELS: Record<RelationshipToChild, string> = {
  [RelationshipToChild.Mother]: "Mother",
  [RelationshipToChild.Father]: "Father",
  [RelationshipToChild.Stepmother]: "Stepmother",
  [RelationshipToChild.Stepfather]: "Stepfather",
  [RelationshipToChild.StepMother]: "Step-Mother",
  [RelationshipToChild.StepFather]: "Step-Father",
  [RelationshipToChild.Grandmother]: "Grandmother",
  [RelationshipToChild.Grandfather]: "Grandfather",
  [RelationshipToChild.Aunt]: "Aunt",
  [RelationshipToChild.Uncle]: "Uncle",
  [RelationshipToChild.Sister]: "Sister",
  [RelationshipToChild.Brother]: "Brother",
  [RelationshipToChild.Cousin]: "Cousin",
  [RelationshipToChild.Niece]: "Niece",
  [RelationshipToChild.Nephew]: "Nephew",
  [RelationshipToChild.Friend]: "Friend",
  [RelationshipToChild.FosterParent]: "Foster Parent",
  [RelationshipToChild.Guardian]: "Guardian",
  [RelationshipToChild.Other]: "Other",
};

export enum CustodyStatus {
  Custodial = "custodial",
  NonCustodial = "non-custodial",
  JointCustodial = "joint-custodial",
  TemporaryCustody = "temporary_custody",
  Unknown = "unknown",
}

export const CUSTODY_STATUS_LABELS: Record<CustodyStatus, string> = {
  [CustodyStatus.Custodial]: "Custodial",
  [CustodyStatus.NonCustodial]: "Non-Custodial",
  [CustodyStatus.JointCustodial]: "Joint Custodial",
  [CustodyStatus.TemporaryCustody]: "Temporary Custody",
  [CustodyStatus.Unknown]: "Unknown",
};

export type ParentStatus = 'active' | 'inactive' | 'pending' | 'suspended';

export interface ParentPreferences {
  communicationMethod?: CommunicationMethod;
  language?: string;
  timezone?: string;
  notificationPreferences?: ParentNotificationPreferences;
  visitPreferences?: ParentVisitPreferences;
}

export type CommunicationMethod = 'email' | 'sms' | 'phone' | 'mail';

export interface ParentNotificationPreferences {
  visitReminders: boolean;
  reportNotifications: boolean;
  paymentNotifications: boolean;
  systemNotifications: boolean;
  emailFrequency: NotificationFrequency;
  smsFrequency: NotificationFrequency;
}

export type NotificationFrequency = 'immediate' | 'daily' | 'weekly' | 'never';

export interface ParentVisitPreferences {
  preferredTimes: string[];
  preferredLocations: string[];
  specialInstructions?: string;
  accessibilityNeeds?: string[];
}

// ============================================================================
// PARENT NOTE
// ============================================================================

/**
 * A timestamped note on a parent's record.
 * Stored as a subcollection: parents/{parentId}/notes/{noteId}
 */
export interface ParentNote extends BaseEntity {
  /** The note content */
  content: string;
  /** User ID who created this note */
  createdByUserId: string;
  /** Display name of creator (denormalized) */
  createdByName?: string;
  /** Account ID for tenant isolation */
  accountId: string;
  /** Parent ID this note belongs to */
  parentId: string;
}

// ============================================================================
// CHILD
// ============================================================================

export interface Child extends BaseEntity {
  firstName: string;
  lastName: string;
  dob?: DateField;
  avatarUrl?: string;
  caseId: string;
  accountId: string;
  gender?: string;
  age?: string;
  createdBy?: string;
  updatedBy?: string;
  hasSpecialNeeds?: boolean;
  specialNeeds?: SpecialNeed[];
  specialNeedsNotes?: string;
  medicalConditions?: MedicalCondition[];
  allergies?: string;
  medications?: Medication[];
  emergencyContacts?: EmergencyContact[];
  schoolInfo?: SchoolInfo;
  preferences?: ChildPreferences;
  status?: ChildStatus;
  notes?: string;

  // Demographics
  sex?: Sex;
  race?: Race;
  primaryLanguage?: PrimaryLanguage;
  primaryLanguageOther?: string;

  // Legal/Attorney information
  /** IDs of attorneys/advocates representing this child (GAL, Minor's Counsel, etc.) */
  attorneyIds?: string[];

  // Custody/Guardian information
  residesWithName?: string;
  legalGuardianName?: string;
  lastContactWithVisitingParent?: DateField;

  // DSS/Social Services information
  isInDSSCustody?: boolean;
  socialWorkerName?: string;
  socialWorkerPhone?: string;
  dssReceivingServices?: boolean;

  // Medical information
  medicalProblems?: string;

  // Victimization/Abuse information
  victimizationType?: VictimizationType;
  victimizationOther?: string;
  hasWitnessedAbuse?: boolean;
  hasIntervenedInAbuse?: boolean;
  interventionDetails?: string;
}

export type ChildStatus = 'active' | 'inactive' | 'aged_out' | 'case_closed';

export interface MedicalCondition {
  condition: string;
  severity: 'low' | 'medium' | 'high';
  description?: string;
  requiresMedication: boolean;
  requiresSpecialCare: boolean;
  notes?: string;
}

export interface Allergy {
  allergen: string;
  severity: 'mild' | 'moderate' | 'severe';
  symptoms: string[];
  treatment?: string;
  notes?: string;
}

export interface Medication {
  name: string;
  dosage: string;
  frequency: string;
  timeOfDay?: string;
  reason: string;
  prescribingDoctor?: string;
  pharmacy?: string;
  notes?: string;
}

export interface SchoolInfo {
  schoolName: string;
  grade: string;
  address?: Address;
  phone?: string;
  contactPerson?: string;
  specialNeeds?: string[];
  schedule?: SchoolSchedule;
}

export interface SchoolSchedule {
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  transportationMethod?: string;
  notes?: string;
}

export interface ChildPreferences {
  interests: string[];
  activities: string[];
  foodPreferences?: string[];
  comfortItems?: string[];
  fears?: string[];
  specialInstructions?: string;
}

// ============================================================================
// CASE PARTICIPANTS
// ============================================================================

export interface CaseParticipant extends BaseEntity {
  caseId: string;
  participantId: string;
  participantType: ParticipantType;
  role: ParticipantRole;
  status: ParticipantStatus;
  startDate: DateField;
  endDate?: DateField;
  permissions: ParticipantPermission[];
  notes?: string;
}

export type ParticipantType = 'parent' | 'child' | 'supervisor' | 'attorney' | 'guardian' | 'other';
export type ParticipantRole = 'primary' | 'secondary' | 'emergency' | 'legal' | 'supervisor';
export type ParticipantStatus = 'active' | 'inactive' | 'pending' | 'removed';

export interface ParticipantPermission {
  type: CasePermissionType;
  granted: boolean;
  grantedAt?: DateField;
  grantedBy?: string;
  expiresAt?: DateField;
}

export type CasePermissionType =
  | 'view_case'
  | 'edit_case'
  | 'view_reports'
  | 'edit_reports'
  | 'view_visits'
  | 'schedule_visits'
  | 'view_billing'
  | 'edit_billing';

// ============================================================================
// CASE DOCUMENTS
// ============================================================================

export interface CaseDocument extends BaseEntity {
  caseId: string;
  type: CaseDocumentType;
  title: string;
  filename: string;
  url: string;
  contentType: string;
  size: number;
  uploadedBy: string;
  metadata?: Record<string, unknown>;
  isPublic: boolean;
  expiresAt?: DateField;
}

export type CaseDocumentType =
  | 'court_order'
  | 'agreement'
  | 'report'
  | 'photo'
  | 'medical_record'
  | 'school_record'
  | 'other';

// ============================================================================
// CASE TIMELINE
// ============================================================================

export interface CaseTimeline extends BaseEntity {
  caseId: string;
  events: TimelineEvent[];
}

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  title: string;
  description?: string;
  timestamp: DateField;
  createdBy: string;
  metadata?: Record<string, unknown>;
  relatedEntityId?: string;
  relatedEntityType?: string;
}

export type TimelineEventType =
  | 'case_created'
  | 'visit_scheduled'
  | 'visit_completed'
  | 'report_generated'
  | 'document_uploaded'
  | 'status_changed'
  | 'participant_added'
  | 'participant_removed'
  | 'payment_received'
  | 'incident_reported';

// ============================================================================
// CASE SUMMARY & STATISTICS
// ============================================================================

export interface CaseSummary extends BaseEntity {
  identifier: string;
  status: CaseStatus;
  type: CaseType;
  totalVisits: number;
  completedVisits: number;
  pendingVisits: number;
  totalReports: number;
  totalDocuments: number;
  totalRevenue: number;
  currency: string;
  participants: {
    parents: number;
    children: number;
    supervisors: number;
  };
  lastActivity: DateField;
}

export interface CaseStatistics extends BaseEntity {
  caseId: string;
  timeFrame: CaseTimeFrame;
  visits: CaseVisitStats;
  reports: ReportStats;
  revenue: CaseRevenueStats;
  participants: ParticipantStats;
  documents: DocumentStats;
}

export type CaseTimeFrame = 'week' | 'month' | 'quarter' | 'year' | 'all';

export interface CaseVisitStats {
  total: number;
  completed: number;
  canceled: number;
  noShow: number;
  averageDuration: number;
  totalDistance: number;
}

export interface ReportStats {
  total: number;
  draft: number;
  completed: number;
  signed: number;
  averageCompletionTime: number;
}

export interface CaseRevenueStats {
  total: number;
  pending: number;
  collected: number;
  overdue: number;
  currency: string;
}

export interface ParticipantStats {
  total: number;
  active: number;
  inactive: number;
  byType: Record<string, number>;
}

export interface DocumentStats {
  total: number;
  byType: Record<string, number>;
  totalSize: number;
  averageSize: number;
}
